import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import toast from "react-hot-toast";
import "./CreateMailAccount.css";

const DOMAINS = ["sdcllp.co.in", "sdcstudio.co", "softvolute.in", "sdcllp.com"];
const ENABLED_DOMAINS = new Set([
  "sdcllp.co.in",
  "sdcstudio.co",
  "softvolute.in",
  "sdcllp.com",
]);
const MAILBOX_REGEX = /^[a-z0-9][a-z0-9._-]{0,63}$/;

const EMPTY_FORM = {
  name: "",
  username: "",
  domain: "sdcllp.co.in",
  password: "",
  confirmPassword: "",
};

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

function errorMessage(error) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.status === 401) {
    return "Session expired. Please log in again.";
  }

  if (error?.response?.status === 403) {
    return "Access denied.";
  }

  if (error?.response?.status === 404) {
    return "This function is not available in the backend yet.";
  }

  return error?.message || "Request failed.";
}

function normalizeAccount(item) {
  const email = String(item.email || item.emailAddress || "")
    .trim()
    .toLowerCase();

  const status = String(item.status || "").toLowerCase();

  return {
    name: item.name || item.employeeName || "—",
    email,
    erpLinked: item.erpLinked === true,
    status:
      item.enabled === false ||
      item.disabled === true ||
      ["disabled", "inactive", "0"].includes(status)
        ? "Disabled"
        : ["active", "enabled", "1"].includes(status) || item.enabled === true
          ? "Active"
          : "Unknown",
  };
}

export default function CreateMailAccount() {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showEditPasswords, setShowEditPasswords] = useState(false);

  const [actionLoading, setActionLoading] = useState("");

  // Existing MailEnable mailbox -> ERP user linking
  const [linkingAccount, setLinkingAccount] = useState(null);
  const [linkPassword, setLinkPassword] = useState("");
  const [showLinkPassword, setShowLinkPassword] = useState(false);

  const username = form.username.trim().toLowerCase();
  const email = `${username || "username"}@${form.domain}`;
  const domainEnabled = ENABLED_DOMAINS.has(form.domain);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMessage("");
  };

  const loadAccounts = useCallback(async () => {
    setListLoading(true);
    setListError("");

    try {
      const response = await api.get("/mail-accounts", authConfig());
      const payload = response.data;

      if (payload?.success === false) {
        throw new Error(payload.message || "Unable to load accounts.");
      }

      const records = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.accounts)
          ? payload.accounts
          : Array.isArray(payload?.data)
            ? payload.data
            : null;

      if (!records) {
        throw new Error("Unexpected account list response.");
      }

      setAccounts(
        records
          .map(normalizeAccount)
          .filter((account) => account.email)
          .sort((a, b) => a.email.localeCompare(b.email)),
      );
    } catch (error) {
      setAccounts([]);
      setListError(errorMessage(error));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();

    return accounts.filter((account) => {
      const matchesSearch =
        account.name.toLowerCase().includes(q) || account.email.includes(q);

      const matchesStatus =
        statusFilter === "All" || account.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [accounts, search, statusFilter]);

  const activeCount = accounts.filter(
    (account) => account.status === "Active",
  ).length;

  const disabledCount = accounts.filter(
    (account) => account.status === "Disabled",
  ).length;

  const linkedCount = accounts.filter((account) => account.erpLinked).length;

  const clearForm = () => {
    if (loading) return;

    setForm({ ...EMPTY_FORM });
    setMessage("");
    setMessageType("");
    setShowPassword(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const name = form.name.trim();
    const mailboxName = form.username.trim().toLowerCase();
    const fullEmail = `${mailboxName}@${form.domain}`;

    const fail = (text) => {
      setMessage(text);
      setMessageType("error");
      toast.error(text);
    };

    if (!name || name.length > 100) {
      return fail("Enter a valid employee name.");
    }

    if (!MAILBOX_REGEX.test(mailboxName)) {
      return fail("Enter a valid mailbox name.");
    }

    if (!domainEnabled) {
      return fail("This email domain is not configured.");
    }

    if (form.password.length < 1 || form.password.length > 30) {
      return fail("Password can be at most 30 characters.");
    }

    if (
      !/[A-Z]/.test(form.password) ||
      !/[a-z]/.test(form.password) ||
      !/[0-9]/.test(form.password) ||
      !/[^A-Za-z0-9]/.test(form.password)
    ) {
      return fail(
        "Password must include uppercase, lowercase, number and special character.",
      );
    }

    if (form.password !== form.confirmPassword) {
      return fail("Passwords do not match.");
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await api.post(
        "/mail-accounts",
        {
          name,
          email: fullEmail,
          password: form.password,
        },
        authConfig(),
      );

      if (response.data?.success !== true) {
        throw new Error(
          response.data?.message || "Account creation was not confirmed.",
        );
      }

      const successMessage =
        response.data.message || `${fullEmail} created successfully.`;

      setMessage(successMessage);
      setMessageType("success");
      toast.success(successMessage);

      setForm({ ...EMPTY_FORM });
      setShowPassword(false);

      await loadAccounts();
    } catch (error) {
      fail(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // LINK EXISTING MAILENABLE ACCOUNT WITH ERP EMPLOYEE
  // ---------------------------------------------------------

  const startLinkERP = (account) => {
    if (account.erpLinked) {
      toast.success("This mailbox is already linked with ERP.");
      return;
    }

    if (account.status !== "Active") {
      toast.error("Only active mailboxes can be linked.");
      return;
    }

    setLinkingAccount(account);
    setLinkPassword("");
    setShowLinkPassword(false);
  };

  const closeLinkERP = () => {
    if (actionLoading) return;

    setLinkingAccount(null);
    setLinkPassword("");
    setShowLinkPassword(false);
  };

  const linkERP = async (event) => {
    event.preventDefault();

    if (!linkingAccount || actionLoading) return;

    if (!linkPassword) {
      toast.error("Enter the existing MailEnable mailbox password.");
      return;
    }

    const mailboxEmail = linkingAccount.email;
    setActionLoading(mailboxEmail);

    try {
      const response = await api.post(
        `/mail-accounts/${encodeURIComponent(mailboxEmail)}/link-erp`,
        { password: linkPassword },
        authConfig(),
      );

      if (response.data?.success !== true) {
        throw new Error(
          response.data?.message || "ERP mailbox linking was not confirmed.",
        );
      }

      toast.success(
        response.data?.message ||
          `${mailboxEmail} linked with ERP successfully.`,
      );

      setLinkingAccount(null);
      setLinkPassword("");
      setShowLinkPassword(false);

      await loadAccounts();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setActionLoading("");
    }
  };

  // ---------------------------------------------------------
  // EXISTING EDIT FUNCTIONALITY
  // ---------------------------------------------------------

  const startEdit = (account) => {
    setEditing(account);
    setEditName(account.name === "—" ? "" : account.name);
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setShowEditPasswords(false);
  };

  const saveEdit = async (event) => {
    event.preventDefault();

    if (!editing || !editName.trim()) return;

    setActionLoading(editing.email);

    try {
      const response = await api.patch(
        `/mail-accounts/${encodeURIComponent(editing.email)}`,
        { name: editName.trim() },
        authConfig(),
      );

      if (response.data?.success !== true) {
        throw new Error("Update was not confirmed by the backend.");
      }

      toast.success("Account updated.");
      setEditing(null);

      await loadAccounts();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setActionLoading("");
    }
  };

  // Password reset remains restricted to the test mailbox.
  const resetPassword = async () => {
    if (!editing || editing.email !== "test@sdcllp.co.in") {
      return toast.error(
        "Password reset testing is enabled only for test@sdcllp.co.in.",
      );
    }

    if (!oldPassword || !newPassword || newPassword !== confirmNewPassword) {
      return toast.error("Enter old password and matching new passwords.");
    }

    if (newPassword === oldPassword) {
      return toast.error("New password must be different from old password.");
    }

    if (
      newPassword.length < 1 ||
      newPassword.length > 30 ||
      !/[A-Z]/.test(newPassword) ||
      !/[a-z]/.test(newPassword) ||
      !/[0-9]/.test(newPassword) ||
      !/[^A-Za-z0-9]/.test(newPassword)
    ) {
      return toast.error(
        "Use up to 30 characters with uppercase, lowercase, number and symbol.",
      );
    }

    setActionLoading(editing.email);

    try {
      const response = await api.post(
        `/mail-accounts/${encodeURIComponent(editing.email)}/reset-password`,
        {
          oldPassword,
          newPassword,
          confirmNewPassword,
        },
        authConfig(),
      );

      if (response.data?.success !== true) {
        throw new Error(
          response.data?.message || "Password reset was not confirmed.",
        );
      }

      toast.success("MailEnable password changed.");

      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setActionLoading("");
    }
  };

  // Preserved from original file. No delete button is exposed.
  const deleteAccount = async (account) => {
    const confirmed = window.confirm(
      `Delete ${account.email}?\n\nThis may permanently remove the mailbox and stored emails. Continue only if you have verified the backup.`,
    );

    if (!confirmed) return;

    setActionLoading(account.email);

    try {
      const response = await api.delete(
        `/mail-accounts/${encodeURIComponent(account.email)}`,
        authConfig(),
      );

      if (response.data?.success !== true) {
        throw new Error("Deletion was not confirmed by the backend.");
      }

      toast.success("Account deleted.");
      await loadAccounts();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setActionLoading("");
    }
  };

  return (
    <main className="mail-account-page">
      <header className="mail-account-header">
        <div>
          <h1>Create Mail Account</h1>
          <p>Create and manage MailEnable email accounts.</p>
        </div>

        <span className="mail-account-tag">HR Management</span>
      </header>

      {/* CREATE MAIL ACCOUNT */}
      <section className="mail-account-card">
        <div className="mail-account-card-title">
          <h2>New Email Account</h2>
          <p>Enter the details of the mailbox to create.</p>
        </div>

        <form onSubmit={submit}>
          <div className="mail-account-grid">
            <label>
              <span>Employee Name *</span>
              <input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. Rahul Prajapati"
                maxLength={100}
                disabled={loading}
                required
              />
            </label>

            <label>
              <span>Mailbox Name *</span>
              <input
                value={form.username}
                onChange={(e) => update("username", e.target.value)}
                placeholder="e.g. rahul.prajapati"
                maxLength={64}
                disabled={loading}
                required
              />
            </label>

            <label>
              <span>Email Domain *</span>
              <select
                value={form.domain}
                onChange={(e) => update("domain", e.target.value)}
                disabled={loading}
              >
                {DOMAINS.map((domain) => (
                  <option
                    key={domain}
                    value={domain}
                    disabled={!ENABLED_DOMAINS.has(domain)}
                  >
                    {domain}
                    {!ENABLED_DOMAINS.has(domain) ? " (Not configured)" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Mail Server</span>
              <input value="MailEnable / SDCLLP" readOnly />
            </label>
          </div>

          <div className="mail-account-preview">
            <small>Email Address</small>
            <strong>{email}</strong>
          </div>

          <div className="mail-account-grid">
            <label>
              <span>Password *</span>
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                placeholder="Up to 30 characters"
                minLength={1}
                maxLength={30}
                autoComplete="new-password"
                disabled={loading}
                required
              />
            </label>

            <label>
              <span>Confirm Password *</span>
              <input
                type={showPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={(e) => update("confirmPassword", e.target.value)}
                placeholder="Re-enter password"
                minLength={1}
                maxLength={30}
                autoComplete="new-password"
                disabled={loading}
                required
              />
            </label>
          </div>

          <label className="mail-account-show-password">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            Show password
          </label>

          {message && (
            <div role="alert" className={`mail-account-message ${messageType}`}>
              {message}
            </div>
          )}

          <div className="mail-account-actions">
            <button
              type="button"
              className="mail-account-reset"
              onClick={clearForm}
              disabled={loading}
            >
              Clear
            </button>

            <button
              type="submit"
              className="mail-account-submit"
              disabled={loading || !domainEnabled}
            >
              {loading ? "Processing..." : "Create Mail Account"}
            </button>
          </div>
        </form>
      </section>

      {/* MAIL ACCOUNT LIST */}
      <section className="mail-account-card mail-account-list-card">
        <div className="mail-account-list-heading">
          <div>
            <h2>Mail Accounts List</h2>
            <p>Mailboxes retrieved from the connected backend.</p>
          </div>

          <button
            type="button"
            className="mail-account-refresh"
            onClick={loadAccounts}
            disabled={listLoading}
          >
            {listLoading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mail-account-stats">
          <div>
            <small>Total Accounts</small>
            <strong>{listError ? "—" : accounts.length}</strong>
          </div>

          <div>
            <small>Active</small>
            <strong>{listError ? "—" : activeCount}</strong>
          </div>

          <div>
            <small>Disabled</small>
            <strong>{listError ? "—" : disabledCount}</strong>
          </div>
        </div>

        <div className="mail-account-list-tools">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email ID..."
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Disabled">Disabled</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        {listError && (
          <div className="mail-account-list-error">
            Unable to load mail accounts: {listError}
          </div>
        )}

        <div className="mail-account-table-wrap">
          <table className="mail-account-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email ID</th>
                <th>Status</th>
                <th>ERP Connection</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredAccounts.map((account) => (
                <tr key={account.email}>
                  <td>{account.name}</td>
                  <td>{account.email}</td>

                  <td>
                    <span
                      className={`mail-account-status ${account.status.toLowerCase()}`}
                    >
                      {account.status}
                    </span>
                  </td>

                  <td>
                    {account.erpLinked ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "6px 10px",
                          background: "#e8f7ed",
                          color: "#18743c",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        ERP Linked
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#707171",
                          fontSize: 12,
                        }}
                      >
                        Not Linked
                      </span>
                    )}
                  </td>

                  <td>
                    <div className="mail-account-row-actions">
                      <button
                        type="button"
                        className="mail-account-edit"
                        onClick={() => startEdit(account)}
                        disabled={!!actionLoading}
                      >
                        Edit
                      </button>

                      {!account.erpLinked && (
                        <button
                          type="button"
                          className="mail-account-edit"
                          onClick={() => startLinkERP(account)}
                          disabled={
                            !!actionLoading || account.status !== "Active"
                          }
                          style={{
                            marginLeft: 8,
                            background: "#343a76",
                            color: "#ffffff",
                          }}
                        >
                          Link ERP
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {!listLoading && !listError && filteredAccounts.length === 0 && (
                <tr>
                  <td colSpan={5} className="mail-account-empty">
                    No mail accounts found.
                  </td>
                </tr>
              )}

              {listLoading && (
                <tr>
                  <td colSpan={5} className="mail-account-empty">
                    Loading mail accounts...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* EXISTING EDIT MODAL */}
      {editing && (
        <div className="mail-account-modal-backdrop">
          <div
            className="mail-account-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit Mail Account"
          >
            <h2>Edit Mail Account</h2>

            <label>
              Email ID (Cannot Change)
              <input value={editing.email} readOnly disabled />
            </label>

            <form onSubmit={saveEdit}>
              <label>
                Employee Name
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={100}
                  required
                />
              </label>

              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 8px",
                    color: "#343a76",
                    fontSize: 16,
                  }}
                >
                  Reset Password
                </h3>

                <p
                  style={{
                    fontSize: 12,
                    color: "#707171",
                    marginBottom: 12,
                  }}
                >
                  Verify the current password before setting a new one.
                </p>

                <label>
                  Old Password
                  <input
                    type={showEditPasswords ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                  />
                </label>

                <label>
                  New Password
                  <input
                    type={showEditPasswords ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Up to 30 characters"
                    minLength={1}
                    maxLength={30}
                    autoComplete="new-password"
                  />
                </label>

                <label>
                  Confirm New Password
                  <input
                    type={showEditPasswords ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                </label>

                <label className="mail-account-show-password">
                  <input
                    type="checkbox"
                    checked={showEditPasswords}
                    onChange={(e) => setShowEditPasswords(e.target.checked)}
                  />
                  Show passwords
                </label>

                <button
                  type="button"
                  className="mail-account-submit"
                  onClick={resetPassword}
                  disabled={
                    !!actionLoading || editing.email !== "test@sdcllp.co.in"
                  }
                  style={{ marginTop: 8 }}
                >
                  Reset Password
                </button>
              </div>

              <div className="mail-account-actions">
                <button
                  type="button"
                  className="mail-account-reset"
                  onClick={() => setEditing(null)}
                  disabled={!!actionLoading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="mail-account-submit"
                  disabled={!!actionLoading}
                >
                  {actionLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW LINK ERP MODAL */}
      {linkingAccount && (
        <div className="mail-account-modal-backdrop">
          <div
            className="mail-account-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Link Mailbox with ERP"
          >
            <h2>Link Mailbox with ERP</h2>

            <p
              style={{
                fontSize: 13,
                color: "#707171",
                lineHeight: 1.6,
                marginBottom: 16,
              }}
            >
              Connect this existing MailEnable mailbox to the ERP employee with
              the same email address. The mailbox will not be recreated and its
              password will not be changed.
            </p>

            <form onSubmit={linkERP}>
              <label>
                Mailbox Email
                <input value={linkingAccount.email} readOnly disabled />
              </label>

              <label>
                Existing Mailbox Password
                <input
                  type={showLinkPassword ? "text" : "password"}
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  placeholder="Enter current MailEnable password"
                  autoComplete="off"
                  required
                />
              </label>

              <label className="mail-account-show-password">
                <input
                  type="checkbox"
                  checked={showLinkPassword}
                  onChange={(e) => setShowLinkPassword(e.target.checked)}
                />
                Show password
              </label>

              <p
                style={{
                  fontSize: 12,
                  color: "#707171",
                  marginTop: 10,
                }}
              >
                The backend will verify the existing mailbox credentials before
                saving the encrypted ERP connection.
              </p>

              <div className="mail-account-actions">
                <button
                  type="button"
                  className="mail-account-reset"
                  onClick={closeLinkERP}
                  disabled={!!actionLoading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="mail-account-submit"
                  disabled={!!actionLoading || !linkPassword}
                >
                  {actionLoading ? "Linking..." : "Link ERP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
